import Vue from 'vue';

import i18n from '../../../core/i18n';
import services from '../../../core/services';
import { DAM_APP_NAME } from '../constants';
import { AttributeFilter } from '../types/attributes';
import { ThumbnailProperty } from '../types/types';
import { IApplicationsService } from './../../../platform/services/types';

const ATTRIBUTE_SEARCH_SEPARATOR = ':';

/**
 *
 * @param searchValue - the user search value
 * @returns if it will be parsed as an attribute-value search item
 */
export function isSearchItemInlineFilter(searchValue?: string) {
  return searchValue && searchValue.includes(ATTRIBUTE_SEARCH_SEPARATOR);
}

/**
 * @param searchValue - the search value to split
 * @returns the attribute name and value
 */
export function extractSearchItemAttributeValue(
  searchValue?: string
): { name: string; value: string } {
  if (!searchValue) {
    return { name: '', value: '' };
  }

  const splitValue = searchValue.split(ATTRIBUTE_SEARCH_SEPARATOR);
  const name = splitValue[0];
  const value = splitValue[1];

  return { name: name || '', value: value || '' };
}

/**
 *
 * @param searchName - the name of the search item
 * @returns if the search item is the filename search prefix
 */
export function isFilenameSearchPrefix(searchName?: string): boolean {
  return searchName === i18n.t('dam.constants.search.filename_prefix');
}

/**
 * Extracts the attribute fieldNames referenced by the gallery thumbnailProperties config
 * in the dam settings
 *
 * @returns the attribute fieldNames used
 */
export function extractBorderColorAttributes() {
  const settings = services
    .getService<IApplicationsService>('applications')
    ?.getAppDeepParameter<Partial<{ [x: string]: ThumbnailProperty }>>(
      DAM_APP_NAME,
      'screensSettings',
      'assets.gallery.thumbnailProperties',
      true
    );

  const thumbnailProperties = settings instanceof Error ? null : settings;
  if (!thumbnailProperties) {
    return [];
  }

  const attributeFieldNames: string[] = [];

  Object.values(thumbnailProperties).forEach(thumbnailProperty => {
    if (thumbnailProperty?.variant.type === 'attributeValue') {
      attributeFieldNames.push(thumbnailProperty.variant.attributeFieldName);
    }
  });

  return attributeFieldNames;
}

export const getSearchItemDisplayValue = (element: AttributeFilter, value: unknown): string => {
  if (element.options.range && Array.isArray(value)) {
    if (value[0] !== null && value[1] !== null) {
      if (element.options._valueLabelRangeFullKey) {
        return i18n.t(element.options._valueLabelRangeFullKey, {
          start: value[0],
          end: value[1],
        }) as string;
      }
    } else if (value[0] !== null && value[1] === null) {
      if (element.options._valueLabelRangeStartKey) {
        return i18n.t(element.options._valueLabelRangeStartKey, {
          start: value[0],
        }) as string;
      }
    } else if (value[0] === null && value[1] !== null) {
      if (element.options._valueLabelRangeEndKey) {
        return i18n.t(element.options._valueLabelRangeEndKey, {
          end: value[1],
        }) as string;
      }
    }
  }

  let valueLabel = '';
  let attrFilterLabel = '';

  if (Array.isArray(value) && value.length === 1) {
    value = value[0];
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if ('_labelKey' in value) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      valueLabel = i18n.t((value as any)._labelKey) as string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if ('label' in value) {
      valueLabel = Vue.filter('piivoTranslate')(value);
    }
  } else {
    valueLabel = Array.isArray(value) && value.length > 1 ? `(${value.length})` : (value as string);
  }

  if (element.labelKey) {
    attrFilterLabel = i18n.t(element.labelKey) as string;
  } else if (element.label) {
    attrFilterLabel = Vue.filter('piivoTranslate')(element);
  } else {
    attrFilterLabel = Vue.filter('piivoTranslate')(element.attribute);
  }

  return `${attrFilterLabel}${ATTRIBUTE_SEARCH_SEPARATOR}${valueLabel}`;
};
